import React, { useContext, useState } from 'react'
import classNames from 'classnames'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import Image from '@toasttab/shared/src/components/atoms/Image/Image'
import { ImageProps } from '@toasttab/shared/src/interfaces/Interface.d'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'
import { OTLContext } from '@toasttab/shared/src/context/OTLContext'
import { BylineProps } from './Byline.d'

const Byline = ({
  author,
  reviewer,
  reviewerClasses,
  showReviewerWebsite
}: BylineProps) => {
  const [showAuthorBio, setShowAuthorBio] = useState(false)
  const [showReviewerBio, setShowReviewerBio] = useState(false)
  const windowSize = useWindowSize()
  const otlParams = useContext(OTLContext)
  const OTLArticleUX: boolean = Boolean(otlParams.isArticle)

  const wrapperClasses = classNames(
    'flex flex-col lg:flex-row gap-[4px] lg:gap-[42px] lg:items-center',
    {
      'mt-4 md:mt-0': OTLArticleUX,
      'my-6 md:my-0': !OTLArticleUX
    }
  )
  const headshotClasses = classNames('w-[32px] h-[32px]', {
    'hidden md:block': OTLArticleUX,
    'max-lg:hidden': !OTLArticleUX
  })
  const authorNameClasses = classNames(
    'font-calibre font-semibold text-[14px] uppercase mr-2',
    {
      'text-blue-75 cursor-pointer': author.bio
    }
  )
  const reviewerNameClasses = classNames(
    'font-calibre font-semibold text-[14px] uppercase mr-2',
    {
      'text-blue-75 cursor-pointer': reviewer.bio
    }
  )

  const openAuthorBio = () => {
    if (author.bio) {
      setShowReviewerBio(false)
      setShowAuthorBio(true)
    }
  }

  const openReviewerBio = () => {
    if (reviewer.bio) {
      setShowAuthorBio(false)
      setShowReviewerBio(true)
    }
  }

  const closeBios = () => {
    setShowAuthorBio(false)
    setShowReviewerBio(false)
  }

  const renderCloseIcon = () => {
    return (
      <svg
        onClick={closeBios}
        className='cursor-pointer'
        width='24'
        height='22'
        viewBox='0 0 24 22'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M5.5 5.04504L18.5 16.6127M18.5 5.04504L5.5 16.6127'
          stroke='#666666'
          strokeWidth='1.5'
          strokeMiterlimit='10'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    )
  }

  const renderBio = (
    type: 'author' | 'reviewer',
    name: string,
    jobTitle: string,
    bio: string,
    headshot: ImageProps,
    websiteLabel?: string,
    websiteURL?: string
  ) => {
    const isMobile = windowSize.width < 425
    const bioClasses = classNames(
      'byline-bio-card absolute p-6 bg-white shadow-elevated rounded-[6px] w-[342px] md:w-[437px] max-lg:max-w-full top-[calc(100%+12px)] border z-20',
      {
        'byline-bio-card-no-image': !headshot,
        relative: isMobile,
        hidden:
          (type === 'author' && !showAuthorBio) ||
          (type === 'reviewer' && !showReviewerBio),
        block:
          (type === 'author' && showAuthorBio) ||
          (type === 'reviewer' && showReviewerBio)
      }
    )

    const headshotCss = classNames('mb-[18px]', {
      'h-[90px] w-[90px] lg:h-[126px] lg:w-[126px]': !OTLArticleUX,
      'h-[65px] w-[65px]': OTLArticleUX
    })

    const headshotImageCss = classNames({
      'h-[90px] w-[90px] lg:h-[126px] lg:w-[126px] rounded-[90px] lg:rounded-[126px]':
        !OTLArticleUX,
      'h-[65px] w-[65px] rounded-[65px]': OTLArticleUX
    })

    return (
      <div className={bioClasses} onMouseLeave={closeBios}>
        <div className='detail-content relative'>
          <div className='absolute top-0 right-0'>{renderCloseIcon()}</div>
          {headshot && (
            <div className={headshotCss}>
              <Image {...headshot} classes={headshotImageCss} />
            </div>
          )}
          <p className='font-calibre font-semibold uppercase text-[24px] mb-1'>
            {name}
          </p>
          <p
            className={`font-calibre font-light italic text-[17px] ${
              !websiteURL ? 'mb-4' : ''
            }`}
          >
            {jobTitle}
          </p>
          {websiteURL && (
            <TextLink
              classes='mb-4 !leading-[18px]'
              fontWeight={['font-normal']}
              target='_blank'
              href={websiteURL}
              text={websiteLabel ? websiteLabel : 'Website'}
            />
          )}
          <p>{bio}</p>
          <div></div>
        </div>
      </div>
    )
  }

  const showReviewerSite =
    reviewer.websiteLabel && reviewer.websiteUrl && showReviewerWebsite

  return (
    <div className={wrapperClasses} data-testid='Byline'>
      <div className='relative'>
        <div className='flex gap-[12px] items-center'>
          {author.headshot && (
            <div className={headshotClasses}>
              <Image
                {...author.headshot}
                classes='h-[32px] w-[32px] rounded-[32px]'
              />
            </div>
          )}
          <p className='font-calibre text-[14px]'>
            <span
              className={authorNameClasses}
              onClick={openAuthorBio}
              onMouseEnter={openAuthorBio}
            >
              {author.name || `Jim McCormick`}
            </span>
            <span className='font-calibre font-light italic'>Author</span>
          </p>
        </div>
        {renderBio(
          'author',
          author.name,
          'Author',
          author.bio,
          author.headshot
        )}
      </div>

      {reviewer.name && (
        <div className={`relative ${reviewerClasses}`}>
          <div className='flex gap-[12px] items-center'>
            {reviewer.headshot && (
              <div className='w-[32px] h-32px] max-lg:hidden'>
                <Image
                  {...reviewer.headshot}
                  classes='h-[32px] w-[32px] rounded-[32px]'
                />
              </div>
            )}
            <p className='font-calibre text-[14px]'>
              <span
                className={reviewerNameClasses}
                onClick={openReviewerBio}
                onMouseEnter={openReviewerBio}
              >
                {reviewer.name}
              </span>
              <span className='font-calibre font-light italic'>
                {reviewer.jobTitle}
                {showReviewerSite && ', '}
              </span>
              {reviewer.websiteLabel &&
                reviewer.websiteUrl &&
                showReviewerWebsite && (
                  <TextLink
                    classes='italic !leading-[18px] whitespace-nowrap'
                    fontSize={['text-sm']}
                    fontWeight={['font-normal']}
                    href={reviewer.websiteUrl}
                    target='_blank'
                    text={reviewer.websiteLabel}
                  />
                )}
            </p>
          </div>
          {renderBio(
            'reviewer',
            reviewer.name,
            reviewer.jobTitle,
            reviewer.bio,
            reviewer.headshot,
            reviewer.websiteLabel,
            reviewer.websiteUrl
          )}
        </div>
      )}
    </div>
  )
}

export default Byline
